import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import type { ProductsData, ProductsPayload } from '@/app/api/search/products/createProductsData.ts';
import { type SearchInfoData, type SearchInfoDetailData } from '@/app/search/types.ts';
import type React from 'react';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { CuriosityParams } from '../Curiosity';
import { PATTERN_LIMIT } from '../Utilities';
import type { Localization } from './Localization';
import getSearchParams from './getSearchParams';
import type { CityCard } from '../utils/buildCityCards/cityCardTypes';
import clientAPICall from './clientAPICall.ts';
import { productsResponseDefault } from '../utils/buildCityCards/ProductsResponse.ts';
import { APIStatusEnum } from './reloadData/APIStatusEnum.ts';

type ShowMoreClick = {
  cityCards: CityCard[];
  showMoreToggle: (show: boolean) => void;
  mainRef: React.RefObject<HTMLDivElement | null>;
  offset: number;
  setOffset: React.Dispatch<React.SetStateAction<number>>;
  curiosityParams: CuriosityParams;
  commonData: CommonData;
  localization: Localization;
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  handleShowMoreResultsReady: (cityCards: CityCard[]) => void;
};

const showMoreClick = async ({
  cityCards,
  showMoreToggle,
  mainRef,
  offset,
  setOffset,
  curiosityParams,
  commonData,
  localization,
  searchInfoData,
  handleShowMoreResultsReady,
}: ShowMoreClick) => {
  mainRef.current?.classList.add('openx-ui-main-loading');
  const mainEl = mainRef.current;

  let storeScrollHeight = document.documentElement.scrollTop;

  const resizeOffset =
    offset + PATTERN_LIMIT - (offset === 0 && cityCards?.filter((x) => x.isMapCard).length > 0 ? 1 : 0);
  const searchParamsFresh = { ...getSearchParams(), [URLParameters.OFFSET]: resizeOffset.toString() };

  const productsRequestPayload: ProductsPayload = {
    searchParams: searchParamsFresh,
    isSearchFromServer: false,
    commonData: { ...commonData, pageStartTime: Date.now() },
    curiosityParams,
    localization,
  };
  const response = await clientAPICall<ProductsPayload, ProductsData>({
    apiName: API_NAME.PRODUCTS,
    payload: productsRequestPayload,
    traceParent: searchInfoData.searchTraceParent,
    commonData,
    defaultResponse: {
      ...productsResponseDefault,
      debugData: {},
      dots: [],
      apiStatus: APIStatusEnum.ERROR,
      apexAvailabilityFlag: false,
    },
  }).then((res) => {
    storeScrollHeight = document.documentElement.scrollTop;
    return res;
  });

  const cityCardsMore: CityCard[] = response?.cityCards || [];

  showMoreToggle(cityCardsMore.length === PATTERN_LIMIT);
  setOffset(resizeOffset);

  handleShowMoreResultsReady(cityCardsMore);

  window.scrollTo(0, storeScrollHeight);

  if (mainEl) mainEl.classList.remove('openx-ui-main-loading');
};

export default showMoreClick;
